import * as React from "react";
import { useCallback, useState } from "react";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { WidgetHeader } from "views/components/widgets";
import { IconButton, IfPermitted } from "components/common";
import WidgetContainer from "views/components/WidgetContainer";
import WidgetContext from "views/components/contexts/WidgetContext";
import WidgetFrame from "views/components/widgets/WidgetFrame";
import EditWidgetFrame from "views/components/widgets/EditWidgetFrame";
import DataLakeLogViewEdit from "data-lake/preview/DataLakeLogViewEdit";
import RetrieveLogsAction from "data-lake/preview/RetrieveLogsAction";
import { useSendWidgetEditTelemetry, useSendWidgetEditCancelTelemetry } from "views/components/widgets/telemetry";
const EditWrapper = ({
  onCancel,
  editing,
  children = void 0,
  onSubmit,
  config,
  configBeforeEdit,
  onChangeWidgetConfig,
  isFetching
}) => {
  if (!editing) {
    return children;
  }
  return /* @__PURE__ */ React.createElement(
    EditWidgetFrame,
    {
      onCancel,
      displaySubmitActions: false,
      onSubmit,
      containerComponent: React.Fragment,
      showQueryControls: false
    },
    /* @__PURE__ */ React.createElement(
      DataLakeLogViewEdit,
      {
        config,
        onCancel,
        isFetching,
        onChange: onChangeWidgetConfig,
        configBeforeEdit
      },
      children
    )
  );
};
const WidgetActionsMenu = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.xxs};
    align-items: center;
  `
);
const DLLogViewWidgetContainer = ({
  children = void 0,
  widget,
  editing,
  loading,
  onToggleEdit,
  onChangeConfig,
  onChangeWidget,
  isFetchingFirstPage,
  effectiveTimerange,
  hasExecutedSearch
}) => {
  var _a;
  const { t } = useTranslation("enterprise_data_lake");
  const sendWidgetEditTelemetry = useSendWidgetEditTelemetry();
  const sendWidgetCancelTelemetry = useSendWidgetEditCancelTelemetry();
  const [configBeforeEdit, setConfigBeforeEdit] = useState();
  const toggleEditing = useCallback(() => {
    if (!editing) {
      sendWidgetEditTelemetry();
      setConfigBeforeEdit(widget.config);
    }
    onToggleEdit();
  }, [editing, onToggleEdit, sendWidgetEditTelemetry, widget.config]);
  const onCancelEdit = useCallback(() => {
    sendWidgetCancelTelemetry();
    onChangeConfig(configBeforeEdit);
    toggleEditing();
  }, [configBeforeEdit, onChangeConfig, sendWidgetCancelTelemetry, toggleEditing]);
  const onSubmitEdit = useCallback(
    (newWidget, hasChanges) => {
      if (hasChanges) {
        return onChangeWidget(newWidget).then(() => toggleEditing());
      }
      toggleEditing();
      return Promise.resolve();
    },
    [onChangeWidget, toggleEditing]
  );
  return /* @__PURE__ */ React.createElement(WidgetContext.Provider, { value: widget }, /* @__PURE__ */ React.createElement(WidgetContainer, { className: "widgetFrame", isFocused: true }, /* @__PURE__ */ React.createElement(WidgetFrame, { widgetId: widget.id }, /* @__PURE__ */ React.createElement(
    WidgetHeader,
    {
      title: t("widget_container.title"),
      hideDragHandle: true,
      loading,
      editing,
      description: ""
    },
    !editing && /* @__PURE__ */ React.createElement(WidgetActionsMenu, null, hasExecutedSearch && /* @__PURE__ */ React.createElement(IfPermitted, { permissions: ["data_lake_archive:restore"] }, /* @__PURE__ */ React.createElement(
      RetrieveLogsAction,
      {
        timerange: effectiveTimerange,
        stream: widget.streams[0],
        fieldFilters: (_a = widget.config.fieldFilters) == null ? void 0 : _a.toArray(),
        fieldsOperator: widget.config.filtersOperator
      }
    )), /* @__PURE__ */ React.createElement(
      IconButton,
      {
        name: "edit_square",
        title: t("widget_container.edit"),
        iconType: "regular",
        onClick: toggleEditing
      }
    ))
  ), /* @__PURE__ */ React.createElement(
    EditWrapper,
    {
      editing,
      onSubmit: onSubmitEdit,
      onCancel: onCancelEdit,
      isFetching: isFetchingFirstPage,
      config: widget.config,
      onChangeWidgetConfig: onChangeConfig,
      configBeforeEdit
    },
    children
  ))));
};
export default DLLogViewWidgetContainer;
